import { useMutation, useQueryClient, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';

import { QueryOption } from '../@types';
import { SKUConfigListResponse, SKUDataType, SKUNameUniqueCheckType } from '../@types/sku.type';
import { createSKUConfig, getAllSKUConfigs, getSKUConfigById, getSKUConfigByName, checkMatchingSKUConfigs, updateSKUConfig, SKUMatchCheckResponse } from '../api/sku';
import { SKUConfigKey, SKUConfigNameUniqueKey, MatchingSKUConfigsKey } from '../configs/sku';

export const useGetSKUConfigs = (queryOption: QueryOption): UseQueryResult<SKUConfigListResponse> => {
  return useQuery({
    queryKey: [SKUConfigKey, queryOption.page, queryOption.pageSize, queryOption.filterModel],
    queryFn: () => getAllSKUConfigs(queryOption)
  });
};

export const useGetSKUConfigById = (id: number): UseQueryResult<SKUDataType | undefined> => {
  return useQuery({
    queryKey: [SKUConfigKey, id],
    queryFn: () => getSKUConfigById(id),
    enabled: id > 0
  });
};

export const useCreateSKUConfig = (): UseMutationResult<SKUDataType, Error, SKUDataType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SKUDataType) => {
      const queryResult = await createSKUConfig(data);
      return queryResult;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SKUConfigKey]
      });
    }
  });
};

export const useUpdateSKUConfig = (): UseMutationResult<SKUDataType, Error, SKUDataType & { id: number }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SKUDataType & { id: number }) => updateSKUConfig(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SKUConfigKey]
      });
    }
  });
};

interface SKUConfigNameCheckData {
  name: string;
  id: number;
}

export const useGetSKUConfigByName = (data: SKUConfigNameCheckData): UseQueryResult<SKUNameUniqueCheckType> => {
  return useQuery({
    queryKey: [SKUConfigNameUniqueKey, data.name, data.id],
    queryFn: () => getSKUConfigByName(data.name, data.id),
    enabled: false
  });
};

export const useCheckMatchingSKUConfigs = (id: number, inPartId: number, outPartId: number): UseQueryResult<SKUMatchCheckResponse | undefined> => {
  return useQuery({
    queryKey: [MatchingSKUConfigsKey, id, inPartId, outPartId],
    queryFn: () => checkMatchingSKUConfigs(inPartId, outPartId),
    enabled: inPartId > 0 && outPartId > 0
  });
};